import React, { useState, useEffect, useRef } from 'react';
import { Alert, Box, Button, Typography, Tabs, TabList, Tab, TabPanel, CircularProgress } from '@mui/joy';
import {
  Package,
  ShoppingCart as ShoppingCartIcon,
  List as ListIcon,
  FileArrowUp as UploadFileIcon,
  Buildings as BusinessIcon,
  Package as Inventory2Icon,
  FileText as DescriptionIcon,
  Stack as LayersIcon,
} from '@phosphor-icons/react';
import { FeatureGate } from '../common/FeatureGate';
import { NotificationProvider } from '../../contexts/NotificationContext';
import NotificationContainer from '../NotificationContainer';
import { AuthProvider, useAuth } from '../../contexts/AuthContext';
import DistributorList from './DistributorList';
import DistributorForm from './DistributorForm';
import DistributorConnectionCard from './DistributorConnectionCard';
import PriceListUpload from './PriceListUpload';
import OrderCreate from './OrderCreate';
import OrderList from './OrderList';
import OrderDetailModal from './OrderDetailModal';
import ProductsList from './ProductsList';
import ProductForm from './ProductForm';
import MaterialsManager from './MaterialsManager';
import InsurancePayoutUpload from '../insurance/InsurancePayoutUpload';
import InsuranceItemManager from '../insurance/InsuranceItemManager';
import InsuranceItemMappingModal from '../insurance/InsuranceItemMappingModal';
import InsurancePayoutItemForm from '../insurance/InsurancePayoutItemForm';
import { distributorsApi, type Distributor, type Order, type PriceListItem } from '../../lib/distributorsApi';
import { type InsurancePayoutItem } from '../../lib/insuranceApi';

const DistributorsContent: React.FC = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const [activeTab, setActiveTab] = useState(0);  // Default to Distributors tab
  const [editingDistributor, setEditingDistributor] = useState<Distributor | null | undefined>(undefined);
  const [connectingDistributor, setConnectingDistributor] = useState<Distributor | null>(null);
  const connectionHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (connectingDistributor) connectionHeading.current?.focus();
  }, [connectingDistributor]);
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<PriceListItem | null>(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [mappingInsuranceItem, setMappingInsuranceItem] = useState<InsurancePayoutItem | null>(null);
  const [editingInsuranceItem, setEditingInsuranceItem] = useState<InsurancePayoutItem | null>(null);
  // OAuth return (?connected=/?error=) and job deep-link (?tab=create&job=) state
  const [oauthResult, setOauthResult] = useState<{ connected: boolean; error?: string } | null>(null);
  const [pageAlert, setPageAlert] = useState<string | null>(null);
  const [initialDraftId, setInitialDraftId] = useState<number | null>(null);
  const [initialJobId, setInitialJobId] = useState<number | null>(null);

  const isAdmin = (user as any)?.role === 'admin' || (user as any)?.is_organization_admin;

  // Handle authentication
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      const currentPath = window.location.pathname + window.location.search;
      const loginUrl = `/login/?next=${encodeURIComponent(currentPath)}`;
      window.location.href = loginUrl;
    }
  }, [isAuthenticated, loading]);

  // Parse OAuth-return and deep-link params once on mount (ProfilePage pattern:
  // read window.location, act, then history.replaceState to clean the URL).
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const oauthError = params.get('error');
    const distributorId = params.get('distributor');
    const tab = params.get('tab');
    const jobId = params.get('job');
    const draftId = params.get('draft');
    if (!connected && !oauthError && !tab) return;

    if (connected === '1' || oauthError) {
      const result = connected === '1'
        ? { connected: true }
        : { connected: false, error: oauthError || 'connection_failed' };
      if (distributorId) {
        // Reopen the distributor form so the connection card shows the outcome.
        distributorsApi.get(Number(distributorId))
          .then((distributor) => {
            setOauthResult(result);
            setEditingDistributor(distributor);
            setActiveTab(0);
          })
          .catch(() => {
            setPageAlert(
              result.connected
                ? 'ABC Supply account connected.'
                : `Connecting the distributor account failed (${result.error}).`,
            );
          });
      } else {
        setPageAlert(
          result.connected
            ? 'ABC Supply account connected.'
            : `Connecting the distributor account failed (${result.error}).`,
        );
      }
    }

    if (tab === 'history') setActiveTab(2);
    if (tab === 'create') {
      setActiveTab(1);
      if (draftId && /^\d+$/.test(draftId)) setInitialDraftId(Number(draftId));
      if (jobId && !Number.isNaN(Number(jobId))) {
        setInitialJobId(Number(jobId));
      }
    }

    if (!draftId) window.history.replaceState({}, document.title, window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, don't render anything (redirect will happen)
  if (!isAuthenticated) {
    return null;
  }

  const handleDistributorEdit = (distributor: Distributor) => {
    setEditingDistributor(distributor);
    setActiveTab(0); // Switch to distributors tab (new index)
  };

  const handleDistributorAdd = () => {
    setEditingDistributor(null);
    setActiveTab(0); // Switch to distributors tab (new index)
  };

  const handleDistributorSave = (saved: Distributor, connectAccount: boolean) => {
    setEditingDistributor(undefined);
    setConnectingDistributor(connectAccount ? saved : null);
    // Ensure we increment refresh trigger to reload the list
    setRefreshTrigger(prev => {
      return prev + 1;
    });
  };

  const handleOrderComplete = () => {
    setInitialDraftId(null);
    setActiveTab(2); // Switch to Order History tab (new index)
    setRefreshTrigger(prev => prev + 1);
  };

  const handlePriceListComplete = () => {
    setActiveTab(0); // Switch to Distributors tab
    setRefreshTrigger(prev => prev + 1);
  };

  const handleViewOrder = (order: Order) => {
    setViewingOrder(order);
    setShowOrderModal(true);
  };

  const handleOrderModalClose = () => {
    setShowOrderModal(false);
    setViewingOrder(null);
  };

  const handleOrderRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleOrderDuplicate = (order: Order) => {
    // Switch to Create Order tab with the duplicated order
    setActiveTab(1); // Create Order tab
    // The duplicated order will already be created in draft status
    setRefreshTrigger(prev => prev + 1);
  };

  const handleProductEdit = (product: PriceListItem) => {
    setEditingProduct(product);
    setShowProductModal(true);
  };

  const handleProductAdd = () => {
    setEditingProduct(null);
    setShowProductModal(true);
  };

  const handleProductSave = () => {
    setRefreshTrigger(prev => prev + 1);
    setShowProductModal(false);
    setEditingProduct(null);
  };

  const handleProductModalClose = () => {
    setShowProductModal(false);
    setEditingProduct(null);
  };

  const handleInsuranceItemSave = () => {
    setRefreshTrigger(prev => prev + 1);
    setEditingInsuranceItem(null);
  };

  // Creation is complete before account authorization starts. Leaving this step
  // keeps the saved distributor available to connect later from Edit.
  if (connectingDistributor && activeTab === 0) {
    return (
      <Box sx={{ width: '100%', minHeight: '100vh', p: 3 }}>
        <Typography ref={connectionHeading} tabIndex={-1} level="h3" sx={{ mb: 1 }}>Connect {connectingDistributor.name}</Typography>
        <Typography sx={{ mb: 3 }}>Distributor saved. Connect your account below, or finish and connect later from Edit.</Typography>
        <DistributorConnectionCard distributorId={connectingDistributor.id} />
        <Button sx={{ mt: 2 }} onClick={() => setConnectingDistributor(null)}>Done</Button>
      </Box>
    );
  }

  // Show form if editing/adding distributor
  if (editingDistributor !== undefined && activeTab === 0) {
    return (
      <Box data-sentry-block sx={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column', p: 3 }}>
        <DistributorForm
          distributor={editingDistributor}
          onSave={handleDistributorSave}
          onCancel={() => setEditingDistributor(undefined)}
          oauthResult={oauthResult}
          onOauthResultConsumed={() => setOauthResult(null)}
        />
      </Box>
    );
  }

  return (
    <Box data-sentry-block sx={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Package size={32} color="var(--joy-palette-primary-500)" />
        <Box>
          <Typography level="h2" component="h1">
            Material Ordering System
          </Typography>
          <Typography level="body-sm" color="neutral">
            Manage distributors, price lists, and orders
          </Typography>
        </Box>
      </Box>

      {pageAlert && (
        <Alert
          color={pageAlert.includes('failed') ? 'danger' : 'success'}
          variant="soft"
          sx={{ mb: 2 }}
          endDecorator={
            <Button
              size="sm"
              variant="plain"
              color={pageAlert.includes('failed') ? 'danger' : 'success'}
              onClick={() => setPageAlert(null)}
            >
              Dismiss
            </Button>
          }
        >
          {pageAlert}
        </Alert>
      )}


      <Tabs 
        value={activeTab} 
        onChange={(event, value) => setActiveTab(value as number)}
        sx={{ bgcolor: 'transparent' }}
      >
        <TabList>
          <Tab>
            <BusinessIcon style={{ marginRight: 8 }} />
            Distributors
          </Tab>
          <Tab>
            <ShoppingCartIcon style={{ marginRight: 8 }} />
            Create Order
          </Tab>
          <Tab>
            <ListIcon style={{ marginRight: 8 }} />
            Order History
          </Tab>
          <Tab>
            <Inventory2Icon style={{ marginRight: 8 }} />
            Products
          </Tab>
          {isAdmin && (
            <>
              <Tab>
                <LayersIcon style={{ marginRight: 8 }} />
                Materials
              </Tab>
              <Tab>
                <UploadFileIcon style={{ marginRight: 8 }} />
                Upload Prices
              </Tab>
              <Tab>
                <DescriptionIcon style={{ marginRight: 8 }} />
                Insurance Items
              </Tab>
              <Tab>
                <UploadFileIcon style={{ marginRight: 8 }} />
                Upload Insurance
              </Tab>
            </>
          )}
        </TabList>

        <TabPanel value={0} sx={{ p: 0, pt: 3 }}>
          <DistributorList
            onEdit={handleDistributorEdit}
            onAdd={handleDistributorAdd}
            refreshTrigger={refreshTrigger}
          />
        </TabPanel>

        <TabPanel value={1} sx={{ p: 0, pt: 3 }}>
          <OrderCreate
            onComplete={handleOrderComplete}
            initialDraftId={initialDraftId}
            initialJobId={initialJobId}
            onInitialJobConsumed={() => setInitialJobId(null)}
          />
        </TabPanel>

        <TabPanel value={2} sx={{ p: 0, pt: 3 }}>
          <OrderList 
            onViewOrder={handleViewOrder}
            refreshTrigger={refreshTrigger}
          />
        </TabPanel>

        <TabPanel value={3} sx={{ p: 0, pt: 3 }}>
          <ProductsList
            onEdit={handleProductEdit}
            onAdd={handleProductAdd}
            refreshTrigger={refreshTrigger}
          />
        </TabPanel>

        {isAdmin && (
          <>
            <TabPanel value={4} sx={{ p: 0, pt: 3 }}>
              <MaterialsManager />
            </TabPanel>

            <TabPanel value={5} sx={{ p: 0, pt: 3 }}>
              <PriceListUpload onComplete={handlePriceListComplete} />
            </TabPanel>

            <TabPanel value={6} sx={{ p: 0, pt: 3 }}>
              <InsuranceItemManager
                onMapItem={(item) => {
                  setMappingInsuranceItem(item);
                }}
                onEditItem={(item) => {
                  setEditingInsuranceItem(item);
                }}
                refreshTrigger={refreshTrigger}
              />
            </TabPanel>

            <TabPanel value={7} sx={{ p: 0, pt: 3 }}>
              <InsurancePayoutUpload onComplete={() => {
                setActiveTab(6); // Switch to Insurance Items tab
                setRefreshTrigger(prev => prev + 1);
              }} />
            </TabPanel>
          </>
        )}
      </Tabs>

      {/* Order Detail Modal */}
      <OrderDetailModal
        open={showOrderModal}
        onClose={handleOrderModalClose}
        order={viewingOrder}
        onRefresh={handleOrderRefresh}
        onDuplicate={handleOrderDuplicate}
      />

      {/* Insurance Item Mapping Modal */}
      <InsuranceItemMappingModal
        open={!!mappingInsuranceItem}
        item={mappingInsuranceItem}
        onClose={() => setMappingInsuranceItem(null)}
        onComplete={() => {
          setMappingInsuranceItem(null);
          setRefreshTrigger(prev => prev + 1);
        }}
      />

      {/* Product Form Modal */}
      <ProductForm
        open={showProductModal}
        onClose={handleProductModalClose}
        product={editingProduct}
        onSave={handleProductSave}
      />

      {/* Insurance Payout Item Form Modal */}
      <InsurancePayoutItemForm
        open={!!editingInsuranceItem}
        onClose={() => setEditingInsuranceItem(null)}
        item={editingInsuranceItem}
        onSave={handleInsuranceItemSave}
      />
    </Box>
  );
};

const DistributorsApp: React.FC = () => {
  return (
    <AuthProvider>
      <NotificationProvider>
        <FeatureGate
          requiredTiers={['pro', 'premium', 'enterprise']}
          featureName="Material Ordering System"
          featureDescription="Streamline your material procurement with our comprehensive distributor management and ordering system."
          benefits={[
            'Manage multiple distributors and suppliers',
            'Track real-time pricing and availability',
            'Create and manage purchase orders',
            'Product catalog management',
            'Insurance item tracking and mapping',
            'Automated price list updates',
            'Order history and analytics'
          ]}
        >
          <NotificationContainer />
          <DistributorsContent />
        </FeatureGate>
      </NotificationProvider>
    </AuthProvider>
  );
};

export default DistributorsApp;